import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ImageUploader } from '@/components/ImageUploader';

const EMPTY = { title: '', description: '', images: [], price: '', promo_label: '', starts_at: '', ends_at: '', status: 'active', cta_label: '', cta_url: '' };
const toLocal = (iso) => (iso ? iso.slice(0, 16) : '');
const toIso = (v) => (v ? new Date(v).toISOString() : null);

const L = ({ label, children }) => (
  <label className="block text-xs font-bold uppercase tracking-wide text-slate-600">{label}<div className="mt-1 font-normal normal-case tracking-normal">{children}</div></label>
);

export const AdminPromoOfferDialog = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (initial) setF({ ...EMPTY, ...initial, price: initial.price ?? '', starts_at: toLocal(initial.starts_at), ends_at: toLocal(initial.ends_at) });
  }, [initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const body = {
      title: f.title.trim(), description: f.description, images: f.images, status: f.status,
      price: f.price === '' ? null : Number(f.price), promo_label: f.promo_label || null,
      starts_at: toIso(f.starts_at), ends_at: toIso(f.ends_at), cta_label: f.cta_label || null, cta_url: f.cta_url || null,
    };
    try {
      if (initial?.offer_id) await api.put(`/admin/promo-offers/${initial.offer_id}`, body);
      else await api.post('/admin/promo-offers', body);
      toast.success('Promoción guardada');
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Revisa los datos de la promoción');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!initial} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-xl" data-testid="admin-promo-offer-dialog">
        <DialogHeader><DialogTitle>{initial?.offer_id ? 'Editar promoción' : 'Nueva promoción'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <L label="Nombre"><Input value={f.title} onChange={set('title')} data-testid="promo-offer-title-input" /></L>
          <L label="Descripción"><Textarea rows={3} value={f.description} onChange={set('description')} data-testid="promo-offer-description-input" /></L>
          <L label="Imágenes"><ImageUploader images={f.images} onChange={(images) => setF((p) => ({ ...p, images }))} max={6} testId="promo-offer-images" /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Precio (COP, opcional)"><Input type="number" min="0" value={f.price} onChange={set('price')} data-testid="promo-offer-price-input" /></L>
            <L label="Etiqueta promocional"><Input placeholder="Ej. 20% OFF" value={f.promo_label} onChange={set('promo_label')} data-testid="promo-offer-label-input" /></L>
            <L label="Inicio"><Input type="datetime-local" value={f.starts_at} onChange={set('starts_at')} data-testid="promo-offer-starts-input" /></L>
            <L label="Fin"><Input type="datetime-local" value={f.ends_at} onChange={set('ends_at')} data-testid="promo-offer-ends-input" /></L>
            <L label="Texto del botón"><Input placeholder="Más información" value={f.cta_label} onChange={set('cta_label')} data-testid="promo-offer-cta-label-input" /></L>
            <L label="Estado">
              <select className="h-9 w-full border border-slate-300 bg-white px-2 text-sm" value={f.status} onChange={set('status')} data-testid="promo-offer-status-select">
                <option value="active">Activa</option><option value="inactive">Inactiva</option>
              </select>
            </L>
          </div>
          <L label="Enlace o contacto (https://, /ruta, mailto:, tel:)"><Input value={f.cta_url} onChange={set('cta_url')} data-testid="promo-offer-cta-url-input" /></L>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="promo-offer-cancel-btn">Cancelar</Button>
          <Button className="rounded-none bg-slate-900 hover:bg-orange-600" disabled={saving || f.title.trim().length < 2} onClick={save} data-testid="promo-offer-save-btn">
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
